import { gql } from "@apollo/client";
import { Spinner } from "components/spinner";
import {
  Books,
  useSearchBooksForSelectorLazyQuery,
  SearchBooksForSelectorQueryResult,
} from "lib/graphql-generated";
import {
  Input,
  inputClassNames,
  labelClassNames,
} from "components/forms/input";
import { useHasClickedOutside } from "lib/hooks/use-has-clicked-outside";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { MultiSelect, MultiSelectData } from "./multi-select";

const SEARCH_BOOK = gql`
  query SearchBooksForSelector($search: String) {
    search_books(args: { search: $search }) {
      title
      isbn
      author
      price
    }
  }
`;

export type BookSelectorValue = MultiSelectData<
  SearchBooksForSelectorQueryResult["data"]["search_books"][0]
>;

export const SerializeBooks = (
  books: Partial<Books>[]
): BookSelectorValue[] => {
  const arr =
    books?.map(
      (b): BookSelectorValue =>
        b && {
          label: b.title,
          key: b.isbn,
          data: { title: b.title, isbn: b.isbn, author: b.author },
        }
    ) ?? [];

  return arr.filter((obj) => obj);
};

interface Props {
  label?: string;
  value: BookSelectorValue[];
  setValue: Dispatch<SetStateAction<MultiSelectData<Partial<Books>>[]>>;
  limit?: number;
  className?: string;
  disabled?: boolean;
  searchFromValues?: BookSelectorValue[];
  order?: boolean;
}

export function BooksSelector({
  label,
  value,
  setValue,
  limit,
  className,
  disabled = false,
  order = false,
  searchFromValues,
}: Props) {
  const [search, { data, loading, error }] = useSearchBooksForSelectorLazyQuery(
    {
      fetchPolicy: "no-cache",
    }
  );
  const [inputHasFocus, setInputHasFocus] = useState<boolean>(false);
  const componentRef = useRef(null);
  const inputRef = useRef(null);
  const hasClickedOutside = useHasClickedOutside(componentRef);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchResults, setSearchResults] = useState<BookSelectorValue[]>([]);
  const innerId = label ? label.replace(" ", "") : "books";
  const addValue = (v: BookSelectorValue) => {
    setValue(() => {
      let arr;
      arr = Boolean(value) ? [].concat(value) : [];
      arr.push(v);
      return arr;
    });
  };

  const removeValue = (v: BookSelectorValue) => {
    setValue(() => {
      let arr = Boolean(value) ? [].concat(value) : [];
      let i = arr.findIndex((inner) => inner === v);
      arr.splice(i, 1);
      return arr;
    });
  };

  const handleMove = (v: BookSelectorValue, move: "up" | "down") => {
    setValue((values) => {
      let arr = [].concat(values);
      let oldIndex = arr.findIndex((inner) => inner === v);
      let moveTo;
      if (move === "up") {
        moveTo = oldIndex === 0 ? arr.length - 1 : oldIndex - 1;
      } else {
        moveTo = oldIndex === arr.length - 1 ? 0 : oldIndex + 1;
      }
      let oldValue = arr.splice(oldIndex, 1)[0];
      arr.splice(moveTo, 0, oldValue);
      return arr;
    });
  };

  useEffect(() => {
    if (hasClickedOutside) {
      setInputHasFocus(false);
    }
  }, [hasClickedOutside]);

  const handleSearch = (searchValue) => {
    if (!searchFromValues && searchValue.length >= 2) {
      search({ variables: { search: searchValue } });
    } else if (searchFromValues && searchValue.length >= 2) {
      setSearchResults(
        searchFromValues.filter((v) => {
          return (
            v.data.title
              .toLowerCase()
              .includes(searchValue.trim().toLowerCase()) ||
            v.data.isbn == searchValue
          );
        })
      );
    }
  };

  return (
    <fieldset ref={componentRef} className={`${className} `}>
      <label
        className={`flex flex-col relative ${labelClassNames}`}
        htmlFor={`${innerId}-multiselect`}>
        {label}
      </label>
      <section className='relative'>
        <div
          className={`mt-1 pl-3 pr-3 py-2 text-base border border-gray-300 focus:outline-none sm:text-sm rounded-md w-full flex flex-col bg-white z-20 focus-within:ring-green-default focus-within:outline-none focus-within:border-green-default ${
            value?.length > 0 && value?.length !== limit
              ? "pb-2"
              : limit && value?.length === limit
              ? "py-2"
              : ""
          }`}>
          <input
            disabled={disabled}
            ref={inputRef}
            className={` w-auto border-none focus:border-none focus:ring-0 p-0 m-0 ${
              limit && value?.length === limit ? "hidden" : ""
            }`}
            type='search'
            id={`${innerId}-multiselect`}
            autoComplete='off'
            autoCorrect='off'
            placeholder={`Search ${
              searchFromValues ? "from selected books" : "by ISBN/Author/Title"
            }`}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(() => {
                let v = e.target.value;

                if (v.trim() !== "") {
                  handleSearch(e.target.value);
                }

                return v;
              });
            }}
            onFocus={() => setInputHasFocus(true)}
          />
          <div className='my-auto mr-0 flex flex-row flex-wrap space-y-2 max-h-96 overflow-y-scroll'>
            {value?.length > 0 &&
              value.map((v, i, a) => (
                <div
                  key={`key_${v.key}`}
                  className={`rounded-sm border-blueGray-500 border box-border bg-opacity-70 flex flex-row w-full hover:border-green-default group overflow-x-hidden first:mt-2`}>
                  <span className='px-2 border-r flex flex-col justify-center'>
                    {i + 1}
                  </span>
                  <section className='flex flex-col flex-grow'>
                    <span className='pl-2 overflow-x-hidden line-clamp-1 bg-blueGray-200'>
                      {v.label}
                    </span>
                    <span className='text-sm text-gray-700 pl-2 border-r-2 border-blueGray-200 group-hover:bg-blueGray-200 line-clamp-1'>
                      {v.data.isbn} - {v.data.author}
                    </span>
                  </section>
                  {order && (
                    <section className='flex flex-col border-r border-blueGray-300'>
                      <button
                        type='button'
                        className='px-2 border-b border-blueGray-300 flex-1 hover:bg-blueGray-200 cursor-pointer'
                        onClick={() => handleMove(v, "up")}>
                        <svg
                          className='w-3'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 15l7-7 7 7'
                          />
                        </svg>
                      </button>
                      <button
                        type='button'
                        className='px-2 flex-1 hover:bg-blueGray-200 cursor-pointer'
                        onClick={() => handleMove(v, "down")}>
                        <svg
                          className='w-3'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                          />
                        </svg>
                      </button>
                    </section>
                  )}
                  <button
                    className='px-4 inline text-coolGray-500 hover:bg-blueGray-200 hover:text-black'
                    onClick={(e) => {
                      e.preventDefault();
                      removeValue(v);
                    }}>
                    x
                  </button>
                </div>
              ))}
          </div>
        </div>
        {(data?.search_books ||
          value?.length > 0 ||
          searchResults.length > 0) &&
          inputHasFocus && (
            <>
              {searchResults?.length > 0 ? (
                <section className='absolute bg-white top-11 mt-px flex flex-col border rounded-md  border-green-default w-full left-0 right-0 mx-auto shadow-xl z-10'>
                  {searchResults?.map((r) => (
                    <label
                      key={`key_${r.data.isbn}`}
                      htmlFor={r.data.isbn}
                      className={`${
                        value?.some(
                          (existingValue) => existingValue.key === r.data.isbn
                        )
                          ? "bg-blueGray-200"
                          : ""
                      } hover:bg-blueGray-200 hover:bg-opacity-50 px-2 py-1 cursor-pointer`}>
                      <input
                        className='sr-only'
                        type='checkbox'
                        name={r.data.isbn}
                        id={r.data.isbn}
                        checked={value?.some(
                          (existingValue) => existingValue.key === r.data.isbn
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSearchValue("");
                            addValue({
                              label: r.data.title,
                              key: r.data.isbn,
                              data: r.data,
                            });
                          } else if (!e.target.checked) {
                            removeValue({
                              label: r.data.title,
                              key: r.data.isbn,
                              data: r.data,
                            });
                          }
                          setSearchValue("");
                          setSearchResults([]);
                          inputRef.current.focus();
                        }}
                      />
                      {r.data.title}
                    </label>
                  ))}
                </section>
              ) : searchValue.length >= 2 && data?.search_books ? (
                <section className='absolute bg-white top-11 mt-px flex flex-col border rounded-md  border-green-default w-full left-0 right-0 mx-auto shadow-xl z-10'>
                  {data.search_books.map((book) => (
                    <label
                      key={`key_${book.isbn}`}
                      htmlFor={book.isbn}
                      className={`${
                        value?.some(
                          (existingValue) => existingValue.key === book.isbn
                        )
                          ? "bg-blueGray-200"
                          : ""
                      } hover:bg-blueGray-200 hover:bg-opacity-50 px-2 py-1 cursor-pointer`}>
                      <input
                        className='sr-only'
                        type='checkbox'
                        name={book.isbn}
                        id={book.isbn}
                        checked={value?.some(
                          (existingValue) => existingValue.key === book.isbn
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // setSearchValue('');
                            addValue({
                              label: book.title,
                              key: book.isbn,
                              data: book,
                            });
                          } else if (!e.target.checked) {
                            removeValue({
                              label: book.title,
                              key: book.isbn,
                              data: book,
                            });
                          }
                          inputRef.current.focus();
                        }}
                      />
                      {book.isbn} - {book.title}
                    </label>
                  ))}
                </section>
              ) : (
                searchValue.length >= 13 &&
                !data?.search_books && (
                  <section className='absolute bg-white top-11 mt-px flex flex-col border rounded-md  border-green-default w-full left-0 right-0 mx-auto shadow-xl z-10'>
                    <span className='flex justify-around pt-2 pb-1'>
                      {loading ? <Spinner /> : "No Results"}
                    </span>
                  </section>
                )
              )}
            </>
          )}
      </section>
    </fieldset>
  );
}

interface SingleSelectProps {
  value: {
    id: string;
    name: string;
    price: number;
  };
  setValue: ({
    id,
    name,
    price,
  }: {
    id: string;
    name: string;
    price: number;
  }) => any;
  id: string;
}

export const SingleBookSelector = ({
  value,
  setValue,
  id,
}: SingleSelectProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [search, { data, loading }] = useSearchBooksForSelectorLazyQuery();

  const handleSearch = (v) => {
    setSearchValue(v);
    if (v.length >= 2) {
      search({
        variables: {
          search: v,
        },
      });
    }
  };

  if (value.name === "") {
    return (
      <div className='relative'>
        <Input
          id={id}
          name={id}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />

        {searchValue !== "" && !!data?.search_books ? (
          <section
            className={`absolute top-full   max-h-96 overflow-y-scroll mt-0.5 w-full text-base border ring-yellow-default border-yellow-default sm:text-sm rounded-md bg-white shadow`}>
            <ul>
              {data.search_books.map((book) => (
                <li key={book.isbn}>
                  <button
                    onClick={() =>
                      setValue({
                        id: book.isbn,
                        name: `[${book.isbn}] ${book.title}`,
                        price: book.price,
                      })
                    }
                    type='button'
                    className='hover:bg-blueGray-200 hover:bg-opacity-50 px-2 py-1 cursor-pointer w-full text-left
                    '>
                    {book.title}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : loading ? (
          <section className={`absolute top-full ${inputClassNames}`}>
            <Spinner />
          </section>
        ) : searchValue !== "" && !data?.search_books ? (
          <section className={`absolute top-full ${inputClassNames}`}>
            No Results
          </section>
        ) : null}
      </div>
    );
  } else {
    return (
      <div className=' h-full flex flex-row justify-between border-b border-gray-300 pb-1 px-2 -mb-4'>
        <span>{value.name}</span>
        <button
          type='button'
          onClick={() => {
            setValue({ name: "", id: "", price: 0 });
            setSearchValue("");
          }}
          className='flex'>
          <svg
            className='w-4 h-4 mt-auto text-gray-500 float-right hover:text-red-default'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
              fillRule='evenodd'
              d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
              clipRule='evenodd'
            />
            <title>remove</title>
          </svg>
          <span className='sr-only'>Remove</span>
        </button>
      </div>
    );
  }
};
